import React, { useState } from 'react';
import { Button, Space, Table, Tag, Tooltip } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GET_PORTAL_ORDER_EXPORT_TASKS } from 'graphql/queries/orderExport';
import { PagedPortalOrderExportTask, PortalOrderExportTask } from 'graphql/types';
import { useOrderExportDownload } from 'hooks/useOrderExportDownload';
import { formatUtcToLocal } from 'utils/dateTime';
import { exportStatusKey, formatFileSize } from 'utils/orderExport';

const DEFAULT_PAGE_SIZE = 20;

/** 状态标签配色：只区分成功/失败/进行中三档，PENDING 与 RUNNING 对客户是同一件事 */
const statusColor = (status: string): string => {
    if (status === 'SUCCESS') return 'green';
    if (status === 'FAILED') return 'red';
    return 'blue';
};

/**
 * 导出记录页。
 *
 * <p><b>渠道级共享</b>：能看到同渠道其他账号提交的任务，也能下载他们的文件（与账单导出一致，
 * P1 已确认）。所以「操作人」列不是可选的装饰——没有它就分不清这一行是谁导的。
 *
 * <p><b>首列展示任务号，这点与账单侧刻意不同</b>：账单导出记录挂在某一张账单下查，账单本身就是
 * 唯一坐标；这里是渠道级平表，而文件名是 orders_export_{customerName}_{时间戳}——
 * 同渠道多人同时导出时 customerName 完全相同、时间戳可能只差几秒，文件名不足以指认某一条记录。
 *
 * <p><b>手动刷新，不自动轮询</b>（P6）：进行中的任务由提交时的等待弹窗负责跟进，
 * 记录页是「回来取文件」的地方，为它挂一个常驻定时器只是在给服务端加无谓的查询。
 */
const OrderExportTaskListPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const { download, downloadingTaskId } = useOrderExportDownload();

    const { data, loading, refetch } = useQuery<{ portalOrderExportTasks: PagedPortalOrderExportTask }>(
        GET_PORTAL_ORDER_EXPORT_TASKS,
        { variables: { page: page - 1, size: pageSize }, fetchPolicy: 'cache-and-network' }
    );

    /**
     * 导出模式列。
     *
     * SELECTED 用 requestedCount（客户勾了多少条）而不是 totalCount（实际写出多少条）：
     * 这一列描述的是「这次导出请求是什么」，不随生成结果变化，进行中时也有值可显示。
     */
    const renderMode = (record: PortalOrderExportTask) =>
        record.exportMode === 'SELECTED'
            ? t('orderExport.mode.selected', { count: record.requestedCount })
            : t('orderExport.mode.filtered');

    const renderAction = (record: PortalOrderExportTask) => {
        if (record.status === 'SUCCESS') {
            return (
                <Button
                    type="link"
                    size="small"
                    style={{ padding: 0 }}
                    icon={<DownloadOutlined />}
                    loading={downloadingTaskId === record.taskId}
                    onClick={() => download(record.taskId)}
                >
                    {t('orderExport.action.download')}
                </Button>
            );
        }
        if (record.status === 'FAILED') {
            /* 刻意不展示后端的失败原因：全是系统内部原因，客户的下一步动作只有「重新导出」 */
            return (
                <Tooltip title={t('orderExport.failedHint')}>
                    <span className="text-sm text-gray-500">{t('orderExport.status.failed')}</span>
                </Tooltip>
            );
        }
        return (
            <Tooltip title={t('orderExport.refreshHint')}>
                <span className="text-sm text-gray-500">{t('orderExport.status.generating')}</span>
            </Tooltip>
        );
    };

    const columns = [
        {
            /* 任务号列窄且固定：它是客户报障时唯一能准确指认某条记录的坐标 */
            title: t('orderExport.columns.taskId'),
            dataIndex: 'taskId',
            key: 'taskId',
            width: 120,
        },
        {
            title: t('orderExport.columns.submittedAt'),
            dataIndex: 'submittedAt',
            key: 'submittedAt',
            render: (val: string | null) => formatUtcToLocal(val),
        },
        {
            title: t('orderExport.columns.operator'),
            dataIndex: 'operatorAccount',
            key: 'operatorAccount',
            render: (val: string | null) => val || '-',
        },
        {
            title: t('orderExport.columns.mode'),
            key: 'exportMode',
            render: (_: unknown, record: PortalOrderExportTask) => renderMode(record),
        },
        {
            title: t('orderExport.columns.status'),
            dataIndex: 'status',
            key: 'status',
            render: (val: string) => <Tag color={statusColor(val)}>{t(exportStatusKey(val))}</Tag>,
        },
        {
            /*
             * 条数 = 实际写出的行数，只有 SUCCESS 时才有意义（未完成时后端下发 0）。
             * 不拿 requestedCount 顶替：那是「请求了多少」，与「导出了多少」是两个口径，
             * 同一列混着显示会让客户以为进行中的任务已经出了结果。勾选条数在「导出模式」列里已有。
             */
            title: t('orderExport.columns.records'),
            dataIndex: 'totalCount',
            key: 'totalCount',
            render: (val: number, record: PortalOrderExportTask) => (record.status === 'SUCCESS' ? val : '-'),
        },
        {
            title: t('orderExport.columns.fileName'),
            dataIndex: 'fileName',
            key: 'fileName',
            render: (val: string | null) => val || '-',
        },
        {
            title: t('orderExport.columns.fileSize'),
            dataIndex: 'fileSize',
            key: 'fileSize',
            render: (val: string | null) => formatFileSize(val),
        },
        {
            title: t('orderExport.columns.action'),
            key: 'action',
            render: (_: unknown, record: PortalOrderExportTask) => renderAction(record),
        },
    ];

    return (
        <div className="bg-white rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-semibold">{t('orderExport.title')}</h1>
                <Space>
                    {/* 本页没有菜单入口（不新增权限节点、不新增菜单），返回按钮是回列表页的常规通路 */}
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/order-query')}>
                        {t('orderExport.action.backToQuery')}
                    </Button>
                    {/* refetch 失败会 reject，errorLink 已弹过提示，这里吞掉以免留下未处理的 promise 拒绝 */}
                    <Button
                        icon={<ReloadOutlined />}
                        loading={loading}
                        onClick={() => {
                            refetch().catch(() => undefined);
                        }}
                    >
                        {t('orderExport.action.refresh')}
                    </Button>
                </Space>
            </div>

            <Table<PortalOrderExportTask>
                columns={columns}
                dataSource={data?.portalOrderExportTasks?.content}
                loading={loading}
                rowKey="taskId"
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: t('orderExport.empty') }}
                pagination={{
                    current: page,
                    pageSize,
                    total: data?.portalOrderExportTasks?.totalElements ?? 0,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '50', '100'],
                    onChange: (nextPage, nextSize) => {
                        setPage(nextPage);
                        setPageSize(nextSize);
                    },
                }}
            />
        </div>
    );
};

export default OrderExportTaskListPage;
